import * as Sentry from '@sentry/nextjs'
import { useEffect, useMemo } from 'react'
import { z } from 'zod'

import {
  TaskDefinition,
  TaskVehicleAvailabilityDefinition,
} from '@/feature/execution-board/types/task'
import { createPaginatedNetworkRequestHook } from '@/utils/createPaginatedNetworkRequestHook'
import { createNetworkRequestHook } from '@/utils/createNetworkRequestHook'

import {
  createSchemaValidationMiddleware,
  NetworkParams,
} from '@/lib/core/hooks/usePaginatedNetworkResource'
import { ListFilters } from '@/lib/core/interfaces/common.types'

export interface TaskFilters extends ListFilters {
  status?: string[]
  orgTo?: number
  orgFrom?: number
  fromDatetime?: string
  toDatetime?: string
  search?: string
  assignedAsset?: number
}

export const useTasksBoardAll = createPaginatedNetworkRequestHook<
  TaskDefinition,
  NetworkParams<TaskFilters>
>('v1/tasks/consolidated/', {
  use: [createSchemaValidationMiddleware(TaskDefinition.parse)],
})

const useRawTaskVehicleAvailability = createNetworkRequestHook<unknown[]>(
  'v1/tasks/vehicle-availability/',
)

/**
 * Splits a vehicle-availability response into the vehicles that match the schema, parsed (so their
 * dates are real Dates), and the ones that do not. A vehicle that fails is left out rather than
 * passed on raw: its assigned tasks could not be checked for overlaps, so offering it would risk a
 * double booking.
 */
export const parseVehicleAvailability = (raw: unknown[]) => {
  const vehicles: TaskVehicleAvailabilityDefinition[] = []
  const failures: { vehicleId: unknown; error: z.ZodError }[] = []

  for (const item of raw) {
    const result = TaskVehicleAvailabilityDefinition.safeParse(item)
    if (result.success) {
      vehicles.push(result.data)
    } else {
      failures.push({ vehicleId: (item as { id?: unknown } | null)?.id, error: result.error })
    }
  }

  return { vehicles, failures }
}

/**
 * The vehicles a task could be assigned to, each with its already-assigned tasks. Validated here,
 * unlike the raw request, so overlap checks work on real Dates; any vehicle that fails validation
 * is reported to Sentry and withheld (see parseVehicleAvailability).
 */
export const useTaskVehicleAvailability = (
  ...args: Parameters<typeof useRawTaskVehicleAvailability>
) => {
  const response = useRawTaskVehicleAvailability(...args)

  const parsed = useMemo(
    () => (response.data ? parseVehicleAvailability(response.data) : undefined),
    [response.data],
  )

  useEffect(() => {
    parsed?.failures.forEach(({ vehicleId, error }) =>
      Sentry.captureException(error, {
        tags: { feature: 'tasks', api: 'v1/tasks/vehicle-availability/' },
        extra: { vehicleId },
      }),
    )
  }, [parsed])

  return { ...response, data: parsed?.vehicles }
}
